import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../lib/api-client';
import { useAuthStore } from '../store/auth';

export const enrollmentKeys = {
  all: ['enrollments'] as const,
  lists: () => [...enrollmentKeys.all, 'list'] as const,
  list: (params: Record<string, unknown>) => [...enrollmentKeys.all, 'list', params] as const,
  details: () => [...enrollmentKeys.all, 'detail'] as const,
  detail: (id: string) => [...enrollmentKeys.details(), id] as const,
  progress: (id: string) => [...enrollmentKeys.detail(id), 'progress'] as const,
};

export function useEnrollments(params?: { userId?: string; programId?: string }, enabled = true) {
  const { user } = useAuthStore();
  const queryParams = { ...params };
  if (user && !queryParams.userId) {
    queryParams.userId = user.id;
  }
  return useQuery({
    queryKey: enrollmentKeys.list(queryParams),
    queryFn: () => apiClient.enrollments.list(queryParams),
    enabled,
  });
}

export function useProgramEnrollment(programId: string) {
  const { user } = useAuthStore();
  return useQuery({
    queryKey: enrollmentKeys.list({ programId, userId: user?.id }),
    queryFn: async () => {
      const response = await apiClient.enrollments.list({ programId, userId: user?.id });
      if (response.length > 1) {
        console.warn(
          `Expected at most 1 enrollment for programId ${programId}, but got ${response.length}`
        );
      }
      if (response[0]) return response[0];

      // A legacy transaction may have marked a registration as APPROVED before
      // its Enrollment row committed. Ask the authenticated API to repair only
      // that approved registration, then re-read the canonical enrollment.
      try {
        await apiClient.registrations.recoverAccess(programId);
      } catch (error: unknown) {
        const statusCode =
          typeof error === 'object' && error !== null && 'statusCode' in error
            ? (error as { statusCode?: unknown }).statusCode
            : undefined;

        // A participant who is not approved must retain the normal access
        // error. Network and server failures still propagate to the error UI.
        if (statusCode === 404 || statusCode === 409) return null;
        throw error;
      }

      const recovered = await apiClient.enrollments.list({ programId, userId: user?.id });
      return recovered[0] || null;
    },
    enabled: !!programId && !!user?.id,
  });
}

export function useEnrollmentProgress(id: string) {
  return useQuery({
    queryKey: enrollmentKeys.progress(id),
    queryFn: () => apiClient.enrollments.getProgress(id),
    enabled: !!id,
  });
}
